import { ref, watch, onMounted, type Ref } from "vue";

// サイドバー配下のタブ選択・タブコンポーネントの初期化を、
// 「レコード（予約/シート/いつでも相談）切替時のみリセットする」という
// 3画面共通のパターンとして抽出したcomposable。
// タブ選択状態の実体（ローカルref か Vuexバックのcomputed か）は呼び出し側が注入する。
type SubTab = { start?: () => void; clear?: () => void } | null;

export interface UseSidebarRecordSyncOptions<TRecord, TId> {
  recordRef: Ref<TRecord | null | undefined>;
  getTargetId: () => TId | null;
  getRecordId: (record: TRecord) => TId;
  tabIdRef: Ref<unknown>; // ローカルrefでもVuexバックcomputedでも可（.valueへのset/getのみ利用）
  defaultTabId: unknown;
  subTabRefs: Ref<SubTab>[];
  onRecordChange?: (context: { targetId: TId | null; record: TRecord | null }) => void;
}

export function useSidebarRecordSync<TRecord, TId>(options: UseSidebarRecordSyncOptions<TRecord, TId>) {
  const { recordRef, getTargetId, getRecordId, tabIdRef, defaultTabId, subTabRefs, onRecordChange } = options;
  // TIdは呼び出し側で決まる汎用型のため、Vueの`UnwrapRef`推論によるミスマッチを避けるべく明示的にキャストする。
  const initializedForId = ref(null) as Ref<TId | null>;

  const initializeTabs = () => {
    const targetId = getTargetId();
    const record = recordRef.value ?? null;
    const isTargetRecord = targetId != null && !!record && getRecordId(record) === targetId;

    // 別レコードへの遷移時のみ、タブ選択と付随状態（幅など）をリセットする
    if (initializedForId.value !== targetId) {
      initializedForId.value = targetId;
      tabIdRef.value = defaultTabId;
      onRecordChange?.({ targetId, record: isTargetRecord ? record : null });
    }

    subTabRefs.forEach((subTabRef) => {
      const target = subTabRef.value;
      if (!target) return;
      if (isTargetRecord) target.start?.();
      else target.clear?.();
    });
  };

  onMounted(initializeTabs);
  watch(recordRef, initializeTabs);

  return { initializeTabs };
}
